import React, { useState, useEffect } from 'react';
import { formatPositionsDisplay } from '../../services/positionAPIs';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Card,
  CardContent,
  Divider,
  IconButton,
  Avatar,
  Stack,
  Paper,
  Alert,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { getStatusColor, calculateDuration, formatDate } from '../leaves/helpers';
import { getAllLeaves, approveLeave, rejectLeave, getEmployeeLeaveBalance, getLeaveById } from '../../services/leavesAPIs';
import { getEmployeeImage } from '../../services/HRstaff';

const LeaveDetailsModal = ({ leaveId, setSelectedLeave, setLeavesData, modalRef, rejectionDialogRef, IsEmployee }) => {
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [leave, setLeave] = useState(null);

  useEffect(() => {
    const fetchLeave = async () => {
      if (!leaveId) return;
      setFetchLoading(true);
      try {
        const data = await getLeaveById(leaveId);
        setLeave(data);
      } catch (error) {
        console.error('Failed to fetch leave:', error);
        setErrorMessage('Failed to load leave details.');
      } finally {
        setFetchLoading(false);
      }
    };
    fetchLeave();
  }, [leaveId]);

  const fileUrl = leave?.content;

  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejectDialogLeaveId, setRejectDialogLeaveId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [leaveBalance, setLeaveBalance] = useState(null);

  const leaveTypeLabels = {
    SICK_LEAVE: 'Sick Leave',
    ANNUAL_LEAVE: 'Annual Leave',
    UNPAID_LEAVE: 'Unpaid Leave',
    MATERNITY_LEAVE: 'Maternity Leave',
  };

  const getApiErrorMessage = (error, fallbackMessage) => {
    const responseData = error?.response?.data;
    if (typeof responseData === 'string' && responseData.trim()) {
      return responseData;
    }
    if (responseData?.message) {
      return responseData.message;
    }
    if (responseData?.error) {
      return responseData.error;
    }
    return error?.message || fallbackMessage;
  };

  const getStatusChipColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const handleOpenRejectDialog = (id) => {
    setRejectDialogLeaveId(id);
    setRejectionReason('');
    setOpenRejectDialog(true);
  };

  const handleCloseRejectDialog = () => {
    setOpenRejectDialog(false);
    setRejectDialogLeaveId(null);
    setRejectionReason('');
  };

  // Fetch employee image
  useEffect(() => {
    const fetchImage = async () => {
      if (leave?.employee?.employeeId) {
        try {
          const url = await getEmployeeImage(leave.employee.employeeId);
          setProfileImageUrl(url);
        } catch (err) {
          console.error('Failed to load employee image:', err);
          setProfileImageUrl(null);
        }
      }
    };
    fetchImage();
  }, [leave?.employee?.employeeId]);

  // Cleanup image URL
  useEffect(() => {
    return () => {
      if (profileImageUrl) URL.revokeObjectURL(profileImageUrl);
    };
  }, [profileImageUrl]);

  // Fetch leave balance on component mount
  const fetchLeaveBalance = async () => {
    try {
      let data = await getEmployeeLeaveBalance(leave.employee.employeeId);
      setLeaveBalance(data);
    } catch (error) {
      console.error('Failed to fetch leave balance', error);
    }
  };

  useEffect(() => {
    if (leave?.employee?.employeeId) {
      fetchLeaveBalance();
    }
  }, [leave?.employee?.employeeId]);

  const handleStatusChange = async (id, newStatus, reason = '') => {
    try {
      setLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      if (newStatus === 'approved') {
        await approveLeave(id);
        setSuccessMessage('Leave approved successfully.');
      } else if (newStatus === 'rejected') {
        await rejectLeave(id, { reason });
        setSuccessMessage(`Leave rejected successfully. Reason: ${reason}`);
      }

      // Re-fetch all leaves to ensure parent table updates
      const updatedLeaves = await getAllLeaves();
      setLeavesData(updatedLeaves);

      // Re-fetch this leave by ID to update modal
      const refreshedLeave = await getLeaveById(id);
      setLeave(refreshedLeave);

      setTimeout(() => setSuccessMessage(''), 8000);
    } catch (error) {
      console.error(`Failed to ${newStatus.toLowerCase()} leave:`, error);
      const fallbackMessage = `Failed to ${newStatus.toLowerCase()} leave. Please try again.`;
      const errorMsg = getApiErrorMessage(error, fallbackMessage);
      setErrorMessage(errorMsg.includes('contract')
        ? 'Cannot approve: Employee has no contract. Please add one.'
        : errorMsg
      );
      setSuccessMessage('');
      setTimeout(() => setErrorMessage(''), 8000);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (rejectionReason.trim()) {
      await handleStatusChange(rejectDialogLeaveId, 'rejected', rejectionReason);
      handleCloseRejectDialog();
    } else {
      setErrorMessage('Please provide a rejection reason.');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleApprove = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!loading && leave?.requestId) {
      await handleStatusChange(leave.requestId, 'approved');
    }
  };

  const handleReject = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!loading && leave?.requestId) {
      handleOpenRejectDialog(leave.requestId);
    }
  };

  const getLeaveTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'annual_leave': return 'primary';
      case 'unpaid_leave': return 'secondary';
      case 'maternity_leave': return 'info';
      case 'sick_leave': return 'error';
      default: return 'default';
    }
  };

  // Open email client with employee's email
  const handleEmailEmployee = () => {
    const email = leave?.employee?.user?.email;
    if (email) {
      window.location.href = `mailto:${encodeURIComponent(email)}`;
    } else {
      setErrorMessage('Employee email not available');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleExportDetails = () => {
    if (!leave?.content) {
      setErrorMessage('No file available to download');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    const docUrl = leave.content;
    const fileName = leave.fileName || 'leave-document';

    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      setErrorMessage('Please allow pop-ups to download the file.');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    newWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Downloading...</title>
      </head>
      <body>
        <script>
          const link = document.createElement('a');
          link.href = '${docUrl.replace(/'/g, "\\'")}';
          link.download = '${fileName.replace(/'/g, "\\'")}';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        </script>
        <p>If download doesn't start, <a href="${docUrl}" download="${fileName}">click here</a>.</p>
      </body>
      </html>
    `);
    newWindow.document.close();
  };

  const handleDialogClose = () => {
    if (!loading) {
      setSelectedLeave(null);
    }
  };

  if (fetchLoading) {
    return (
      <Dialog
        open
        ref={modalRef}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, p: 3, textAlign: 'center' }
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress size={36} />
          <Typography variant="body1" color="text.secondary">
            Loading leave details...
          </Typography>
        </Box>
      </Dialog>
    );
  }

  if (!leave) return null;

  return (
    <>
      <Dialog
        open={Boolean(leave)}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && !loading) {
            handleDialogClose();
          }
        }}
        ref={modalRef}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={loading}
        PaperProps={{
          sx: { borderRadius: 2, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" component="h2" fontWeight="600">
              Leave Details
            </Typography>
            <IconButton onClick={handleDialogClose} disabled={loading} sx={{ color: 'grey.500' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: 3,
            py: 2,
            maxHeight: '70vh',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {/* Employee Information Card */}
          <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar
                  src={profileImageUrl || leave.employee?.image || undefined}
                  sx={{ bgcolor: (profileImageUrl || leave.employee?.image) ? 'transparent' : 'primary.main', mr: 2 }}
                >
                  {!(profileImageUrl || leave.employee?.image) && <PersonIcon />}
                </Avatar>
                <Typography variant="h6" fontWeight="600">
                  Employee Information
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Full Name
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {leave.employee?.firstName} {leave.employee?.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Department
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {(typeof leave.employee?.department === 'object' ? leave.employee?.department?.name : leave.employee?.department) || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Position
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {formatPositionsDisplay(leave.employee) || (leave.employee?.position || 'N/A')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Leave Balance, Details, and Justification Grid */}
          <Grid container spacing={3} sx={{ width: '100%', m: 0 }}>
            {/* Leave Balance */}
            <Grid item xs={12} md={leave.content ? 4 : 6} sx={{ pl: '0 !important' }}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <AccessTimeIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" fontWeight="600">
                    Leave Balance
                  </Typography>
                </Box>
                <Stack spacing={1.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Annual Vacation
                    </Typography>
                    <Chip
                      label={`${leaveBalance?.ANNUAL_LEAVE || 0} days`}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Unpaid Vacation
                    </Typography>
                    <Chip
                      label="Unlimited"
                      sx={{ ml: 1 }}
                      color="secondary"
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Leave Details */}
            <Grid item xs={12} md={leave.content ? 4 : 6} sx={{ pl: { md: 2, xs: 0 } }}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <CalendarIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" fontWeight="600">
                    Leave Details
                  </Typography>
                </Box>
                <Stack spacing={1.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                    <Typography variant="body2" color="text.secondary">
                      Type
                    </Typography>
                    <Chip
                      label={leaveTypeLabels[leave.type] || leave.type}
                      color={getLeaveTypeColor(leave.type)}
                      size="small"
                    />
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={leave.status}
                      color={getStatusChipColor(leave.status)}
                      size="small"
                    />
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Start Date
                    </Typography>
                    <Typography variant="body2" fontWeight="500" sx={{ ml: 1 }}>
                      {formatDate(leave.startDate)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      End Date
                    </Typography>
                    <Typography variant="body2" fontWeight="500" sx={{ ml: 1 }}>
                      {formatDate(leave.endDate)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body2" fontWeight="500" sx={{ ml: 1 }}>
                      {leave.type === 'UNPAID_LEAVE'
                        ? `${leave.hours || (calculateDuration(leave.startDate, leave.endDate) * 8)} hours`
                        : `${calculateDuration(leave.startDate, leave.endDate)} days`}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Submitted
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {formatDate(leave.createdAt)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Justification File */}
            {leave.content && (
              <Grid item xs={12} md={4} sx={{ pl: { md: 2, xs: 0 } }}>
                <Card elevation={1} sx={{ borderRadius: 2, height: '100%', p: 0 }}>
                  <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', '&:last-child': { pb: 2 } }}>
                    <Typography variant="h6" fontWeight="600" gutterBottom textAlign="center">
                      Justification File
                    </Typography>
                    <Box sx={{ width: '100%', height: '100%', maxHeight: 400, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', borderRadius: 1.5 }}>
                      {leave.content.endsWith('.pdf') ? (
                        <embed src={fileUrl} type="application/pdf" width="100%" height="350px" />
                      ) : (
                        <img
                          src={fileUrl}
                          alt="Justification"
                          style={{ maxWidth: '100%', maxHeight: 350, objectFit: 'contain', borderRadius: 8 }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Reason Section */}
          <Card elevation={1} sx={{ mt: 3, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Reason for Leave
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {leave.justification || 'No reason provided'}
              </Typography>
            </CardContent>
          </Card>

          {/* Comments Section */}
          {leave.comments && (
            <Card elevation={1} sx={{ mt: 3, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  Comments
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {leave.comments}
                </Typography>
              </CardContent>
            </Card>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
          {errorMessage && (
            <Alert severity="error" sx={{ width: '100%', mb: 1 }}>
              {errorMessage}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ width: '100%', mb: 1 }}>
              {successMessage}
            </Alert>
          )}

          {!IsEmployee && leave.status === 'pending' && (
            <Box display="flex" gap={1} mr="auto">
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={handleApprove}
                disabled={loading}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                {loading ? 'Approving...' : 'Approve'}
              </Button>

              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={handleReject}
                disabled={loading}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Reject
              </Button>
            </Box>
          )}

          {!IsEmployee && (
            <>
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
                disabled={loading}
                onClick={handleEmailEmployee}
              >
                Email Employee
              </Button>
              {leave?.content && (
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                  disabled={loading}
                  onClick={handleExportDetails}
                >
                  Export Doc
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={openRejectDialog} onClose={handleCloseRejectDialog} ref={rejectionDialogRef}>
        <DialogTitle>Reject Leave Request</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for Rejection"
            type="text"
            fullWidth
            variant="outlined"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseRejectDialog} disabled={loading} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleRejectSubmit} color="error" variant="contained" disabled={loading} sx={{ textTransform: 'none' }}>
            {loading ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LeaveDetailsModal;
